import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { inventoryApi } from '../../services/inventoryApi.js';

const empty = () => ({ supplierId: '', productId: '', unitPrice: '', validFrom: '', validTo: '' });
const emptyLine = () => ({ deliveryDate: '', plannedQuantity: '' });

export function SchedulingAgreementCreateModal({ open, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(empty);
  const [lines, setLines] = useState([emptyLine()]);
  const [error, setError] = useState(null);

  const products = useQuery({ queryKey: ['inventory', 'products'], queryFn: () => inventoryApi.listProducts({ limit: 200 }).then((r) => r.items), enabled: open });
  const suppliers = useQuery({ queryKey: ['inventory', 'suppliers'], queryFn: () => inventoryApi.listSuppliers().then((r) => r.items), enabled: open });

  useEffect(() => { if (open) { setForm(empty()); setLines([emptyLine()]); setError(null); } }, [open]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setLine = (i, k) => (e) => setLines((arr) => arr.map((l, idx) => (idx === i ? { ...l, [k]: e.target.value } : l)));

  const save = useMutation({
    mutationFn: (body) => inventoryApi.createSchedulingAgreement(body),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['inventory', 'scheduling-agreements'] }); onClose(); },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  function submit(e) {
    e.preventDefault();
    setError(null);
    if (!form.supplierId || !form.productId) { setError(t('lp.error.required')); return; }
    const cleanLines = lines.filter((l) => l.deliveryDate && Number(l.plannedQuantity) > 0).map((l) => ({ deliveryDate: l.deliveryDate, plannedQuantity: Number(l.plannedQuantity) }));
    if (cleanLines.length === 0) { setError(t('lp.error.lines')); return; }
    save.mutate({ supplierId: form.supplierId, productId: form.productId, unitPrice: Number(form.unitPrice) || 0, validFrom: form.validFrom || null, validTo: form.validTo || null, lines: cleanLines });
  }

  return (
    <Modal open={open} onClose={onClose} title={t('lp.modal.title')}
      footer={<><Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="primary" onClick={submit} disabled={save.isPending}>{save.isPending ? `${t('common.save')}…` : t('common.save')}</Button></>}>
      <form onSubmit={submit} className="form-stack">
        <div className="field-row">
          <label className="field"><span className="field-label">{t('lp.col.supplier')}</span>
            <select className="input" value={form.supplierId} onChange={set('supplierId')} required>
              <option value="">-</option>
              {(suppliers.data || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>
          <label className="field" style={{ maxWidth: 130 }}><span className="field-label">{t('lp.field.price')}</span>
            <input className="input num" type="number" min="0" step="0.01" value={form.unitPrice} onChange={set('unitPrice')} />
          </label>
        </div>
        <label className="field"><span className="field-label">{t('lp.col.product')}</span>
          <select className="input" value={form.productId} onChange={set('productId')} required>
            <option value="">-</option>
            {(products.data || []).map((p) => <option key={p.id} value={p.id}>{p.sku} · {p.name}</option>)}
          </select>
        </label>
        <div className="field-row">
          <label className="field"><span className="field-label">{t('lp.field.from')}</span>
            <input className="input" type="date" value={form.validFrom} onChange={set('validFrom')} />
          </label>
          <label className="field"><span className="field-label">{t('lp.field.to')}</span>
            <input className="input" type="date" value={form.validTo} onChange={set('validTo')} />
          </label>
        </div>

        <div className="field">
          <span className="field-label">{t('lp.field.lines')}</span>
          {lines.map((l, i) => (
            <div key={i} className="field-row" style={{ marginBottom: 'var(--space-2)' }}>
              <input className="input" type="date" value={l.deliveryDate} onChange={setLine(i, 'deliveryDate')} />
              <input className="input num" style={{ maxWidth: 140 }} type="number" min="0.0001" step="any" placeholder={t('lp.field.qty')} value={l.plannedQuantity} onChange={setLine(i, 'plannedQuantity')} />
            </div>
          ))}
          <Button type="button" size="sm" variant="ghost" onClick={() => setLines((a) => [...a, emptyLine()])}>{t('lp.field.addLine')}</Button>
        </div>
        {error && <div className="alert danger">{error}</div>}
      </form>
    </Modal>
  );
}
